// PantryReveal — searchable chip-input for ingredient dislikes.
//
// UX: Selected items float to the top as removable chips. Typing filters
// the suggestion list in real-time. If typed text doesn't match any known
// option the user sees "+ Add '<text>'" so custom ingredients are supported.
//
// Props:
//   selected  — string[], currently selected items
//   options   — string[], the preset suggestion list
//   onChange  — (newSelected: string[]) => void
//   placeholder — input placeholder text

import { useState, useRef, useMemo } from "react";
import { C } from "../theme.js";

export function PantryReveal({ selected = [], options = [], onChange, placeholder = "Search ingredients…" }) {
  const [query, setQuery] = useState("");
  const inputRef = useRef(null);

  const toggle = (item) => {
    if (selected.includes(item)) {
      onChange(selected.filter(x => x !== item));
    } else {
      onChange([...selected, item]);
      setQuery("");
      inputRef.current?.focus();
    }
  };

  const addCustom = (raw) => {
    const item = raw.trim().toLowerCase();
    if (!item || selected.includes(item)) { setQuery(""); return; }
    onChange([...selected, item]);
    setQuery("");
    inputRef.current?.focus();
  };

  // Filter suggestions: exclude already-selected, match query.
  const suggestions = useMemo(() => {
    const q = query.trim().toLowerCase();
    const unselected = options.filter(o => !selected.includes(o));
    if (!q) return unselected;
    return unselected.filter(o => o.toLowerCase().includes(q));
  }, [query, options, selected]);

  // Show "+ Add" when query doesn't exactly match any existing option.
  const showAddCustom = query.trim().length > 1 &&
    !options.some(o => o.toLowerCase() === query.trim().toLowerCase()) &&
    !selected.includes(query.trim().toLowerCase());

  return (
    <div>
      {/* ── Selected chips ─────────────────────────────────────────────── */}
      {selected.length > 0 && (
        <div style={{
          display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 16,
          padding: "12px 14px", borderRadius: 14,
          background: C.greenPale, border: `1px solid ${C.greenMid}`,
        }}>
          {selected.map(item => (
            <button
              key={item}
              onClick={() => toggle(item)}
              style={{
                display: "inline-flex", alignItems: "center", gap: 6,
                padding: "6px 12px", borderRadius: 99,
                border: `1.5px solid ${C.green}`,
                background: C.white, color: C.green,
                fontWeight: 700, fontSize: 13, cursor: "pointer",
              }}
            >
              {item}
              <span style={{
                display: "inline-flex", alignItems: "center", justifyContent: "center",
                width: 16, height: 16, borderRadius: "50%",
                background: C.green, color: C.white, fontSize: 10, fontWeight: 900,
                lineHeight: 1,
              }}>✕</span>
            </button>
          ))}
        </div>
      )}

      {/* ── Search input ───────────────────────────────────────────────── */}
      <div style={{ position: "relative", marginBottom: 14 }}>
        <span style={{
          position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)",
          fontSize: 16, color: C.grey, pointerEvents: "none",
        }}>🔍</span>
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={e => setQuery(e.target.value)}
          onKeyDown={e => {
            if (e.key === "Enter" && showAddCustom) addCustom(query);
            if (e.key === "Escape") setQuery("");
          }}
          placeholder={placeholder}
          style={{
            width: "100%", padding: "12px 14px 12px 42px",
            borderRadius: 12, border: `1.5px solid ${C.border}`,
            background: C.white, fontSize: 15, color: C.charcoal,
            outline: "none", transition: "border-color .18s",
          }}
          onFocus={e => (e.target.style.borderColor = C.green)}
          onBlur={e  => (e.target.style.borderColor = C.border)}
        />
        {query && (
          <button
            onClick={() => setQuery("")}
            style={{
              position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)",
              background: "none", border: "none", cursor: "pointer",
              color: C.grey, fontSize: 18, lineHeight: 1, padding: 4,
            }}
          >✕</button>
        )}
      </div>

      {/* ── Custom-add prompt ──────────────────────────────────────────── */}
      {showAddCustom && (
        <button
          onClick={() => addCustom(query)}
          style={{
            display: "flex", alignItems: "center", gap: 8,
            padding: "10px 16px", marginBottom: 12, borderRadius: 12,
            border: `1.5px dashed ${C.green}`, background: C.greenPale,
            color: C.green, fontWeight: 700, fontSize: 14, cursor: "pointer",
            width: "100%",
          }}
        >
          <span style={{ fontSize: 18 }}>＋</span>
          Add "<strong>{query.trim()}</strong>"
        </button>
      )}

      {/* ── Suggestion chips ───────────────────────────────────────────── */}
      {suggestions.length > 0 ? (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
          {suggestions.map(item => (
            <button
              key={item}
              onClick={() => toggle(item)}
              style={{
                padding: "8px 14px", borderRadius: 99,
                border: `1.5px solid ${C.border}`,
                background: C.white, color: C.charcoal,
                fontWeight: 600, fontSize: 14, cursor: "pointer",
                transition: "all .15s",
              }}
              onMouseEnter={e => {
                e.currentTarget.style.borderColor = C.green;
                e.currentTarget.style.background  = C.greenPale;
                e.currentTarget.style.color       = C.green;
              }}
              onMouseLeave={e => {
                e.currentTarget.style.borderColor = C.border;
                e.currentTarget.style.background  = C.white;
                e.currentTarget.style.color       = C.charcoal;
              }}
            >
              {item}
            </button>
          ))}
        </div>
      ) : query && !showAddCustom ? (
        <p style={{ color: C.grey, fontSize: 14, textAlign: "center", padding: "16px 0" }}>
          No matches — press Enter or tap "Add" to add a custom item.
        </p>
      ) : null}

      {!query && selected.length === 0 && (
        <p style={{ color: C.grey, fontSize: 13, marginTop: 8 }}>
          Tap anything to exclude it, or type to search. Custom ingredients are fine too.
        </p>
      )}
    </div>
  );
}
